import React, { useEffect } from "react";
import type { AdPositionEnum } from "../../../client/api";
import { InternalAdRenderer } from "../InternalAdRenderer";
import { trackAmplitude } from "../../../config/amplitude";
import AdSenseAd from "../AdSenseAd";
import { ADSENSE_CONFIG } from "../../../config/adsenseConfig";
import { WebAdSlot } from "../WebAdSlot";
import { useAdSelection } from "../../../hooks/useAdSelection";
import { useAdSenseAd } from "../../../hooks/useAdSenseLoadStatus";

type AdSlotProps = {
  type: AdPositionEnum;
  className?: string;
  priority?: "internal" | "adsense" | "auto";
};

export function AdSlot({
  type,
  className,
  priority = "auto",
}: AdSlotProps) {
  const hasAdSenseConfig = Boolean(ADSENSE_CONFIG[type]);
  const skipSelection = priority === "adsense" || priority === "internal";

  const { selectedAd, pro } = useAdSelection({
    type,
    canShowAdSense: hasAdSenseConfig,
    skipSelection,
  });
  const shouldMonitorAdSense = priority === "auto" && hasAdSenseConfig && !selectedAd;
  const { status: adSenseLoadStatus, ref: adSenseInsRef } = useAdSenseAd(
    shouldMonitorAdSense
  );

  useEffect(() => {
    if (selectedAd && !pro) {
      trackAmplitude("ad_view", { type, name: selectedAd.name });
    }
  }, [selectedAd, pro, type]);

  if (pro) {
    return null;
  }

  if (priority === "adsense" && hasAdSenseConfig) {
    return <AdSenseAd position={type} className={className} />;
  }

  if (priority === "internal") {
    return <WebAdSlot type={type} className={className} />;
  }

  if (priority === "auto") {
    if (selectedAd && selectedAd.imageDesktop) {
      return (
        <InternalAdRenderer
          ad={selectedAd}
          position={selectedAd.position}
          className={className}
        />
      );
    }

    if (hasAdSenseConfig) {
      if (adSenseLoadStatus === "failed") {
        return <WebAdSlot type={type} className={className} />;
      }

      const shouldShow = adSenseLoadStatus === "loaded";

      return (
        <div
          className={className}
          style={
            shouldShow
              ? undefined
              : {
                  height: 0,
                  overflow: "hidden",
                  opacity: 0,
                  pointerEvents: "none",
                }
          }
        >
          <AdSenseAd position={type} ref={adSenseInsRef} />
        </div>
      );
    }

    return <WebAdSlot type={type} className={className} />;
  }

  return <WebAdSlot type={type} className={className} />;
}
